import React, { useEffect, useState, useId } from 'react';

const OmniSlider: React.FC<{
  setOrder: (order: number) => void;
  initialOrder: number;
  title: string;
}> = ({ setOrder, initialOrder, title }) => {
  const [value, setValue] = useState(0);
  const [lastValue, setLastValue] = useState<number>(0);
  // Create a unique ID for this slider instance
  const sliderId = useId().replace(/:/g, '-');

  useEffect(() => {
    if (initialOrder) {
      setValue(initialOrder);
      setLastValue(initialOrder);
    }
  }, [initialOrder]);

  const handleChange = (event: any) => {
    setValue(parseInt(event.target.value, 10));
  };

  const handleSubmit = (event: any) => {
    const currentValue = parseInt(event.target.value, 10);
    if (lastValue === currentValue) return;

    setOrder(currentValue);

    setLastValue(currentValue);
  };

  const handleIncrement = () => {
    const newValue = Math.min(value + 1, 200);
    if (newValue !== value) {
      setValue(newValue);
      setOrder(newValue);
      setLastValue(newValue);
    }
  };

  const handleDecrement = () => {
    const newValue = Math.max(value - 1, -200);
    if (newValue !== value) {
      setValue(newValue);
      setOrder(newValue);
      setLastValue(newValue);
    }
  };

  const getColor = (currentValue: number): string => {
    if (currentValue < 0) return '#F37766';
    if (currentValue === 0) return '#9FA5B1';
    return '#00AB9B'; // Default positive color
  };

  return (
    <div className={`omni-slider-${sliderId}`} style={styles.container}>
      <style>{thumbStyles(getColor(value), sliderId)}</style> {/* Pass color and sliderId to thumbStyles */}
      <div style={styles.textContainer}>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%' }}>
          <div style={styles.title}>{title}</div>
          <div style={styles.discount(getColor(value))}>{(value / 2).toFixed(1)}%</div>{' '}
          {/* Display value directly and apply color */}
        </div>
        <div style={styles.sliderContainer}>
          <button style={styles.button('#9FA5B1')} onClick={handleDecrement} aria-label="Decrease value">
            −
          </button>
          <input
            type="range"
            min="-200" // Update min range
            max="200" // Update max range
            value={value}
            onChange={handleChange}
            onMouseUp={handleSubmit}
            style={styles.slider(value, getColor(value))} // Pass color to slider style
            className={`slider-${sliderId}`}
          />
          <button style={styles.button('#9FA5B1')} onClick={handleIncrement} aria-label="Increase value">
            +
          </button>
        </div>
      </div>
    </div>
  );
};

const thumbStyles = (color: string, sliderId: string) => `
  .omni-slider-${sliderId} input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 1.6vh;
    height: 1.6vh;
    background: ${color}; /* Use dynamic color */
    cursor: pointer;
    border-radius: 50%;
    border: none;
  }

  .omni-slider-${sliderId} input[type="range"]::-moz-range-thumb {
    width: 1.6vh;
    height: 1.6vh;
    background: ${color}; /* Use dynamic color */
    cursor: pointer;
    border-radius: 50%;
    border: none;
  }

  .omni-slider-${sliderId} input[type="range"]::-ms-thumb {
    width: 1.6vh;
    height: 1.6vh;
    background: ${color}; /* Use dynamic color */
    cursor: pointer;
    border-radius: 50%;
    border: none;
  }
`;

const styles = {
  sliderContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: '0.8vh',
  },
  button: (color: string): React.CSSProperties => ({
    border: 'none',
    color: color,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '1.6vh',
    fontWeight: 'bold',
    margin: 0,
    padding: 0,
    lineHeight: 1,
  }),
  slider: (value: number, color: string): React.CSSProperties => {
    // Map value from -100 to 100 range to 0% to 100% for gradient
    const percentage = (value + 200) / 4;
    const grayColor = 'rgba(16, 29, 59, 0.1)';
    let backgroundGradient: string;

    if (value > 0) {
      // Gradient from center to right
      backgroundGradient = `linear-gradient(90deg, ${grayColor} 50%, ${color} 50%, ${color} ${percentage}%, ${grayColor} ${percentage}%)`;
    } else if (value < 0) {
      // Gradient from left to center
      backgroundGradient = `linear-gradient(90deg, ${grayColor} ${percentage}%, ${color} ${percentage}%, ${color} 50%, ${grayColor} 50%)`;
    } else {
      // Exactly at center (value is 0), slider is all gray
      backgroundGradient = `linear-gradient(90deg, ${grayColor} 100%, ${grayColor} 100%)`; // Or simply grayColor
    }

    return {
      width: '100%',
      appearance: 'none' as 'none',
      height: '0.4vh',
      borderRadius: '0.5vh',
      background: backgroundGradient, // Use the calculated gradient
      outline: 'none',
      opacity: '0.9',
      transition: 'opacity 0.2s, background 0.2s', // Added background transition
    };
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  textContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'start',
    gap: '0.4vh',
  },
  title: {
    fontSize: '1.4vh',
    fontWeight: '700',
    color: '#101D3B',
  },
  discount: (color: string): React.CSSProperties => ({
    fontSize: '1.6vh',
    fontWeight: '700',
    color: color, // Use dynamic color
  }),
};

export default OmniSlider;
